import React, { useState, useRef, useCallback, useMemo } from "react";
import { Handle, Position, useViewport } from "@xyflow/react";
import { ListVideo, Film, Loader2, Wand2, X } from "lucide-react";

const StoryboardEpisodeNode = ({ data, id, selected }) => {

  const [shots, setShots] = useState(data.shots || []);
  const [aspectRatio, setAspectRatio] = useState(data.aspectRatio || "16:9");
  // 片5 语义缩放：zoom < 0.45 简卡（只留标题 + 计数，缩略图网格 CSS 隐藏）
  const { zoom } = useViewport();
  const isSimple = zoom < 0.45;
  const [refPopover, setRefPopover] = useState(null); // { shotIndex, x, y, type: 'character'|'scene'|'prop' }
  const [mentionPopover, setMentionPopover] = useState(null); // { shotIndex, x, y, cursorPos } - @ 提及弹窗
  const mentionIndexRef = useRef(0);
  const [mentionIndex, setMentionIndex] = useState(0); // 渲染期使用的镜像值（不直接读 ref）
  const textareaRefs = React.useRef({}); // 存储每个 textarea 的 ref
  const mentionListRef = useRef(null); // @ 菜单滚动容器
  const mentionItemRefs = useRef([]); // @ 菜单每项 ref

  const connectedImages = useMemo(() => data.connectedImages || [], [data.connectedImages]);

  // 外部 data 变化时同步本地编辑态（渲染期调整，避免 effect 级联渲染）
  const [lastDataSync, setLastDataSync] = useState({ shots: data.shots, aspectRatio: data.aspectRatio });
  if (data.shots !== lastDataSync.shots || data.aspectRatio !== lastDataSync.aspectRatio) {
    setLastDataSync({ shots: data.shots, aspectRatio: data.aspectRatio });
    setShots(data.shots || []);
    setAspectRatio(data.aspectRatio || "16:9");
  }

  const syncToData = () => {
    if (data.onUpdate) {
      data.onUpdate(id, { ...data, shots, aspectRatio });
    }
  };

  // 编辑字段
  const handleFieldChange = (shotIndex, field, value) => {
    const newShots = [...shots];
    newShots[shotIndex][field] = value;
    setShots(newShots);
  };

  const handleFieldBlur = () => {
    syncToData();
  };

  // 处理 @ 提及输入
  const handlePromptInput = (e, shotIndex) => {
    const value = e.target.value;
    const cursorPos = e.target.selectionStart;

    // 检查光标前是否有 @
    const textBeforeCursor = value.substring(0, cursorPos);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');

    if (lastAtIndex !== -1) {
      const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
      // 如果 @ 后面没有空格，显示提及菜单
      if (!textAfterAt.includes(' ') && !textAfterAt.includes('\n')) {
        const textarea = e.target;
        const rect = textarea.getBoundingClientRect();
        // 简单估算光标位置（实际位置计算较复杂，这里用固定偏移）
        setMentionPopover({
          shotIndex,
          x: rect.left + 10,
          y: rect.top + 60,
          cursorPos: lastAtIndex,
          searchText: textAfterAt.toLowerCase(),
        });
        mentionIndexRef.current = 0;
        setMentionIndex(0);
        return;
      }
    }

    setMentionPopover(null);
  };

  // 选择 @ 提及的资产
  const handleMentionSelect = (shotIndex, assetIndex, assetName, assetType) => {
    if (!mentionPopover) return;

    const newShots = [...shots];
    const shot = newShots[shotIndex];
    const currentPrompt = shot.imagePrompt || '';

    // 替换 @ 及其后面的文本为 @名称
    const beforeAt = currentPrompt.substring(0, mentionPopover.cursorPos);
    const afterCursor = currentPrompt.substring(textareaRefs.current[shotIndex]?.selectionStart || currentPrompt.length);
    const newPrompt = beforeAt + '@' + assetName + ' ' + afterCursor;

    shot.imagePrompt = newPrompt;

    // 根据资产类型自动添加到对应的参考图数组
    if (assetType === 'character') {
      const current = shot.refCharacterIndexes || [];
      if (!current.includes(assetIndex)) {
        shot.refCharacterIndexes = [...current, assetIndex];
      }
    } else if (assetType === 'scene') {
      const current = shot.refSceneIndexes || [];
      if (!current.includes(assetIndex)) {
        shot.refSceneIndexes = [...current, assetIndex];
      }
    } else if (assetType === 'prop') {
      const current = shot.refPropIndexes || [];
      if (!current.includes(assetIndex)) {
        shot.refPropIndexes = [...current, assetIndex];
      }
    } else {
      // 如果没有类型信息，默认添加到角色
      const current = shot.refCharacterIndexes || [];
      if (!current.includes(assetIndex)) {
        shot.refCharacterIndexes = [...current, assetIndex];
      }
    }

    setShots(newShots);
    setMentionPopover(null);

    // 聚焦回 textarea
    setTimeout(() => {
      const textarea = textareaRefs.current[shotIndex];
      if (textarea) {
        textarea.focus();
        const newCursorPos = beforeAt.length + assetName.length + 2; // @ + 名称 + 空格
        textarea.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);

    syncToData();
  };

  // 过滤后的 mention 列表
  const filteredMentions = useMemo(() => {
    if (!mentionPopover || !connectedImages.length) return [];
    const q = mentionPopover.searchText || '';
    return connectedImages
      .map((img, i) => ({ ...img, index: i }))
      .filter(img => !q || img.name.toLowerCase().includes(q));
  }, [mentionPopover, connectedImages]);

  // 更新 @ 菜单选中项
  const updateMentionIndex = useCallback((newIndex) => {
    const oldIndex = mentionIndexRef.current;
    mentionIndexRef.current = newIndex;
    setMentionIndex(newIndex);
    const oldItem = mentionItemRefs.current[oldIndex];
    const newItem = mentionItemRefs.current[newIndex];
    if (oldItem) {
      oldItem.style.backgroundColor = 'transparent';
      const nameEl = oldItem.querySelector('[data-name]');
      if (nameEl) nameEl.style.color = 'var(--color-text-primary)';
    }
    if (newItem) {
      newItem.style.backgroundColor = 'var(--color-bg-tertiary)';
      const nameEl = newItem.querySelector('[data-name]');
      if (nameEl) nameEl.style.color = 'var(--color-accent-primary)';
      newItem.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, []);

  // @ 菜单键盘导航（普通函数即可：仅作为 onKeyDown 事件处理器使用）
  const handleMentionKeyDown = (e, shotIndex) => {
    if (!mentionPopover || filteredMentions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      updateMentionIndex((mentionIndexRef.current + 1) % filteredMentions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      updateMentionIndex((mentionIndexRef.current - 1 + filteredMentions.length) % filteredMentions.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const item = filteredMentions[mentionIndexRef.current];
      if (item) handleMentionSelect(shotIndex, item.index, item.name, item.assetType);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setMentionPopover(null);
    }
  };

  // 参考图选择（支持多选）
  const handleRefImageSelect = (shotIndex, refIndex, type) => {
    const newShots = [...shots];
    const shot = newShots[shotIndex];

    if (type === 'character') {
      const current = shot.refCharacterIndexes || [];
      if (refIndex === null) {
        shot.refCharacterIndexes = [];
      } else {
        if (current.includes(refIndex)) {
          shot.refCharacterIndexes = current.filter(i => i !== refIndex);
        } else {
          shot.refCharacterIndexes = [...current, refIndex];
        }
      }
    } else if (type === 'scene') {
      const current = shot.refSceneIndexes || [];
      if (refIndex === null) {
        shot.refSceneIndexes = [];
      } else {
        if (current.includes(refIndex)) {
          shot.refSceneIndexes = current.filter(i => i !== refIndex);
        } else {
          shot.refSceneIndexes = [...current, refIndex];
        }
      }
    } else if (type === 'prop') {
      const current = shot.refPropIndexes || [];
      if (refIndex === null) {
        shot.refPropIndexes = [];
      } else {
        if (current.includes(refIndex)) {
          shot.refPropIndexes = current.filter(i => i !== refIndex);
        } else {
          shot.refPropIndexes = [...current, refIndex];
        }
      }
    }

    setShots(newShots);
    if (data.onUpdate) {
      data.onUpdate(id, { ...data, shots: newShots, aspectRatio });
    }
  };

  // 批量生图
  const handleBatchGenerate = () => {
    if (data.onGenerate) {
      data.onGenerate(id, { ...data, shots, aspectRatio });
    }
  };

  const accentColor = 'var(--color-accent-primary)';
  const handleStyle = {
    width: 14, height: 14,
    backgroundColor: accentColor,
    border: '2px solid color-mix(in srgb, var(--color-accent-contrast) 80%, transparent)',
    borderRadius: '50%',
    cursor: 'crosshair',
    boxShadow: `0 0 6px ${accentColor}60`,
  };

  const isGenerating = data.isGenerating;
  const generatedCount = shots.filter(s => s.generatedImage).length;

  // ========== 渲染 ==========
  return (
    <div className={`relative node-expand-wrapper transition-[width] duration-200${isSimple ? " episode-zoom-simple" : ""}`} style={{ width: data.expanded ? 950 : 340 }}>
      <Handle className="nodrag" type="target" position={Position.Left} id="left" style={handleStyle} />
      <Handle className="nodrag" type="source" position={Position.Right} id="right" style={handleStyle} />

      <div className="space-y-2">
        {/* 标题 — 卡片外 */}
        <div className="flex items-center justify-between px-1">
          <div className="flex items-center gap-1.5">
            <ListVideo size={14} style={{ color: accentColor }} />
            <span className="text-xs font-bold" style={{ color: 'var(--color-text-primary)' }}>
              {data.episodeName || `第${data.episodeId}集`}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-2xs" style={{ color: 'var(--color-text-muted)' }}>
              {generatedCount}/{shots.length} 已生图
            </span>
            {isGenerating && <Loader2 size={12} className="animate-spin" style={{ color: accentColor }} />}
          </div>
        </div>

        {/* 缩略图卡片 — 独立圆角（简卡模式 CSS 隐藏，不卸载 DOM） */}
        <div className={isGenerating ? "episode-detail canvas-node-card--generating" : "episode-detail"} style={{
          borderRadius: 14,
          overflow: 'hidden',
          border: selected ? `2px solid ${accentColor}` : `1.5px solid ${accentColor}25`,
          // P4 多层阴影系统；选中加 accent 外环（原 ${accentColor}30 拼接对 var() 无效，改用 --color-accent-light）
          boxShadow: selected
            ? '0 0 0 3px var(--color-accent-light), inset 0 1px 0 rgba(255,255,255,0.05), 0 12px 24px -8px rgba(0,0,0,0.5), 0 32px 64px -24px rgba(0,0,0,0.35)'
            : 'inset 0 1px 0 rgba(255,255,255,0.05), 0 12px 24px -8px rgba(0,0,0,0.5), 0 32px 64px -24px rgba(0,0,0,0.35)',
          transition: 'border 0.2s, box-shadow 0.2s',
          background: 'var(--color-bg-secondary)',
        }}>
          <div className="px-3 py-2">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 3 }}>
              {shots.slice(0, 9).map((shot, i) => (
                <div key={i} className="rounded overflow-hidden" style={{ aspectRatio: '16/9', backgroundColor: 'var(--color-bg-tertiary)' }}>
                  {shot.generatedImage ? (
                    <img src={shot.generatedImage} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Film size={12} style={{ color: 'var(--color-text-muted)' }} />
                    </div>
                  )}
                </div>
              ))}
            </div>
            {shots.length > 9 && (
              <div className="text-center text-2xs mt-1.5" style={{ color: 'var(--color-text-muted)' }}>
                +{shots.length - 9} 更多分镜
              </div>
            )}
          </div>
        </div>

        {/* 功能区 — 点击节点后展开（大表格） */}
        {data.expanded && (
          <div style={{
            borderRadius: 14,
            background: 'var(--color-bg-secondary)',
            border: `1.5px solid ${accentColor}40`,
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.05), 0 12px 24px -8px rgba(0,0,0,0.5), 0 32px 64px -24px rgba(0,0,0,0.35)',
            overflow: 'hidden',
            maxHeight: '60vh',
          }}>
            {/* 工具栏 */}
            <div className="px-3 py-2 flex items-center justify-between" style={{ background: accentColor }}>
              <span className="text-xs font-bold text-[var(--color-accent-contrast)]">{shots.length} 个分镜</span>
              <div className="flex items-center gap-2">
                <div className="flex gap-1">
                  {["16:9", "9:16", "1:1"].map(r => (
                    <button key={r} onClick={(e) => { e.stopPropagation(); setAspectRatio(r); syncToData(); }}
                      className="nodrag px-2 py-0.5 rounded-full text-2xs font-semibold transition-all"
                      style={{
                        backgroundColor: aspectRatio === r ? 'var(--color-accent-contrast)' : 'transparent',
                        color: aspectRatio === r ? accentColor : 'var(--color-accent-contrast)',
                        border: `1px solid ${aspectRatio === r ? 'var(--color-accent-contrast)' : 'color-mix(in srgb, var(--color-accent-contrast) 30%, transparent)'}`,
                      }}>
                      {r}
                    </button>
                  ))}
                </div>
                <button
                  className="nodrag px-3 py-1 rounded-lg text-xs font-bold transition-all flex items-center gap-1"
                  style={{ backgroundColor: 'white', color: accentColor, opacity: isGenerating ? 0.6 : 1 }}
                  onClick={(e) => { e.stopPropagation(); handleBatchGenerate(); }}
                  disabled={isGenerating}
                >
                  {isGenerating ? (
                    <><Loader2 size={12} className="animate-spin" />{data.generatingStatus || "生成中..."}</>
                  ) : (
                    <><Wand2 size={12} />全部生图</>
                  )}
                </button>
              </div>
            </div>

            {/* 主体区域 */}
            <div className="overflow-auto nowheel p-3" style={{ maxHeight: 'calc(60vh - 44px)' }}>
        {/* 参考图池（sticky） */}
        {connectedImages.length > 0 && (
          <div className="sticky top-0 z-20 px-3 py-2 mb-3 rounded-lg" style={{ backgroundColor: 'var(--color-bg-primary)', border: '1px solid var(--color-border-primary)', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
            <label className="text-2xs font-bold mb-1 block" style={{ color: 'var(--color-text-muted)' }}>参考图池</label>
            <div className="flex gap-1 flex-wrap">
              {connectedImages.map((img, i) => (
                <div key={i} className="w-8 h-8 rounded overflow-hidden" style={{ border: '1px solid var(--color-border-primary)' }} title={img.name}>
                  <img src={img.url} alt="" className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 表格 */}
        <table className="text-2xs" style={{ borderCollapse: 'collapse', minWidth: '100%' }}>
          <thead className="sticky z-10" style={{ top: connectedImages.length > 0 ? '76px' : '0', backgroundColor: 'var(--color-bg-primary)', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
            <tr>
              <th className="px-3 py-2 text-left whitespace-nowrap" style={{ borderBottom: '1px solid var(--color-border-primary)', color: 'var(--color-text-secondary)' }}>镜号</th>
              <th className="px-3 py-2 text-left whitespace-nowrap" style={{ borderBottom: '1px solid var(--color-border-primary)', color: 'var(--color-text-secondary)', minWidth: '80px' }}>景别</th>
              <th className="px-3 py-2 text-left whitespace-nowrap" style={{ borderBottom: '1px solid var(--color-border-primary)', color: 'var(--color-text-secondary)', minWidth: '70px' }}>时长</th>
              <th className="px-3 py-2 text-left whitespace-nowrap" style={{ borderBottom: '1px solid var(--color-border-primary)', color: 'var(--color-text-secondary)', minWidth: '250px' }}>画面描述</th>
              <th className="px-3 py-2 text-left whitespace-nowrap" style={{ borderBottom: '1px solid var(--color-border-primary)', color: 'var(--color-text-secondary)', minWidth: '200px' }}>光影氛围</th>
              <th className="px-3 py-2 text-left whitespace-nowrap" style={{ borderBottom: '1px solid var(--color-border-primary)', color: 'var(--color-text-secondary)', minWidth: '150px' }}>音效</th>
              <th className="px-3 py-2 text-left whitespace-nowrap" style={{ borderBottom: '1px solid var(--color-border-primary)', color: 'var(--color-text-secondary)', minWidth: '200px' }}>对白</th>
              <th className="px-3 py-2 text-left whitespace-nowrap" style={{ borderBottom: '1px solid var(--color-border-primary)', color: 'var(--color-text-secondary)', minWidth: '300px' }}>图片提示词</th>
              <th className="px-3 py-2 text-left whitespace-nowrap" style={{ borderBottom: '1px solid var(--color-border-primary)', color: 'var(--color-text-secondary)', minWidth: '300px' }}>视频提示词</th>
              <th className="px-3 py-2 text-left whitespace-nowrap" style={{ borderBottom: '1px solid var(--color-border-primary)', color: 'var(--color-text-secondary)', minWidth: '140px' }}>角色参考图</th>
              <th className="px-3 py-2 text-left whitespace-nowrap" style={{ borderBottom: '1px solid var(--color-border-primary)', color: 'var(--color-text-secondary)', minWidth: '140px' }}>场景参考图</th>
              <th className="px-3 py-2 text-left whitespace-nowrap" style={{ borderBottom: '1px solid var(--color-border-primary)', color: 'var(--color-text-secondary)', minWidth: '140px' }}>道具参考图</th>
            </tr>
          </thead>
          <tbody>
            {shots.map((shot, idx) => {
              const selectedCharacterRefs = (shot.refCharacterIndexes || []).map(i => connectedImages[i]).filter(Boolean);
              const selectedSceneRefs = (shot.refSceneIndexes || []).map(i => connectedImages[i]).filter(Boolean);
              const selectedPropRefs = (shot.refPropIndexes || []).map(i => connectedImages[i]).filter(Boolean);
              return (
                <tr key={shot.shotId} className="hover:bg-opacity-50" style={{ borderBottom: '1px solid var(--color-border-primary)' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                  <td className="px-3 py-2 font-bold whitespace-nowrap" style={{ color: accentColor }}>{shot.shotNumber}</td>
                  <td className="px-3 py-2 whitespace-nowrap" style={{ color: 'var(--color-text-secondary)' }}>
                    <input
                      className="nodrag nowheel w-full px-2 py-1 rounded text-2xs"
                      style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-secondary)', minWidth: '70px' }}
                      value={shot.shot || ''}
                      onChange={(e) => handleFieldChange(idx, 'shot', e.target.value)}
                      onBlur={handleFieldBlur}
                      placeholder="景别"
                    />
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap" style={{ color: 'var(--color-text-secondary)' }}>
                    <input
                      className="nodrag nowheel w-full px-2 py-1 rounded text-2xs"
                      style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-secondary)', minWidth: '60px' }}
                      value={shot.time || ''}
                      onChange={(e) => handleFieldChange(idx, 'time', e.target.value)}
                      onBlur={handleFieldBlur}
                      placeholder="时长"
                    />
                  </td>
                  <td className="px-3 py-2" style={{ color: 'var(--color-text-secondary)' }}>
                    <textarea
                      className="nodrag nowheel w-full px-2 py-1 rounded text-2xs resize-vertical"
                      style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-secondary)', minWidth: '240px', minHeight: '60px', lineHeight: '1.4' }}
                      value={shot.content || ''}
                      onChange={(e) => handleFieldChange(idx, 'content', e.target.value)}
                      onBlur={handleFieldBlur}
                      placeholder="画面描述"
                      rows={3}
                    />
                  </td>
                  <td className="px-3 py-2" style={{ color: 'var(--color-text-secondary)' }}>
                    <textarea
                      className="nodrag nowheel w-full px-2 py-1 rounded text-2xs resize-vertical"
                      style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-secondary)', minWidth: '190px', minHeight: '60px', lineHeight: '1.4' }}
                      value={shot.visualContent || ''}
                      onChange={(e) => handleFieldChange(idx, 'visualContent', e.target.value)}
                      onBlur={handleFieldBlur}
                      placeholder="光影氛围"
                      rows={3}
                    />
                  </td>
                  <td className="px-3 py-2" style={{ color: 'var(--color-text-secondary)' }}>
                    <textarea
                      className="nodrag nowheel w-full px-2 py-1 rounded text-2xs resize-vertical"
                      style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-secondary)', minWidth: '140px', minHeight: '60px', lineHeight: '1.4' }}
                      value={shot.audio || ''}
                      onChange={(e) => handleFieldChange(idx, 'audio', e.target.value)}
                      onBlur={handleFieldBlur}
                      placeholder="音效"
                      rows={3}
                    />
                  </td>
                  <td className="px-3 py-2" style={{ color: 'var(--color-text-secondary)' }}>
                    <textarea
                      className="nodrag nowheel w-full px-2 py-1 rounded text-2xs resize-vertical"
                      style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-secondary)', minWidth: '190px', minHeight: '60px', lineHeight: '1.4' }}
                      value={shot.content || ''}
                      onChange={(e) => handleFieldChange(idx, 'content', e.target.value)}
                      onBlur={handleFieldBlur}
                      placeholder="对白"
                      rows={3}
                    />
                  </td>
                  <td className="px-3 py-2" style={{ color: 'var(--color-text-secondary)' }}>
                    <div className="relative">
                      <textarea
                        ref={(el) => textareaRefs.current[idx] = el}
                        className="nodrag nowheel w-full px-2 py-1 rounded text-2xs resize-vertical"
                        style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-secondary)', minWidth: '290px', minHeight: '60px', lineHeight: '1.4' }}
                        value={shot.imagePrompt || ''}
                        onChange={(e) => {
                          handleFieldChange(idx, 'imagePrompt', e.target.value);
                          handlePromptInput(e, idx);
                        }}
                        onKeyDown={(e) => handleMentionKeyDown(e, idx)}
                        onBlur={handleFieldBlur}
                        placeholder="图片提示词（输入 @ 可选择参考图）"
                        rows={3}
                      />
                      {/* @ 提及弹窗 */}
                      {mentionPopover?.shotIndex === idx && filteredMentions.length > 0 && (
                        <>
                          <div className="fixed inset-0 z-30" onClick={() => setMentionPopover(null)} />
                          <div ref={mentionListRef} className="absolute z-40 mt-1 rounded-lg p-2 animate-in fade-in zoom-in-95 duration-150" style={{ boxShadow: 'var(--shadow-dropdown)', backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-primary)', minWidth: '240px', maxHeight: '300px', overflowY: 'auto' }}>
                            <div className="text-2xs font-bold mb-2 px-1" style={{ color: 'var(--color-text-muted)' }}>
                              选择参考图
                            </div>
                            <div className="space-y-1">
                              {filteredMentions.map((img, i) => (
                                  <button
                                    key={img.index}
                                    ref={el => mentionItemRefs.current[i] = el}
                                    className="nodrag w-full px-2 py-1 rounded flex items-center gap-2 text-left transition-colors"
                                    style={{ backgroundColor: i === 0 && mentionIndex === 0 ? 'var(--color-bg-tertiary)' : 'transparent' }}
                                    onMouseEnter={() => updateMentionIndex(i)}
                                    onClick={() => handleMentionSelect(idx, img.index, img.name, img.assetType)}
                                  >
                                    <div className="w-8 h-8 rounded overflow-hidden flex-shrink-0" style={{ border: '1px solid var(--color-border-primary)' }}>
                                      <img src={img.url} alt="" className="w-full h-full object-cover" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div data-name className="text-2xs font-medium truncate" style={{ color: i === 0 && mentionIndex === 0 ? accentColor : 'var(--color-text-primary)' }}>{img.name}</div>
                                      {img.assetType && (
                                        <div className="text-2xs" style={{ color: 'var(--color-text-muted)' }}>
                                          {img.assetType === 'character' ? '角色' : img.assetType === 'scene' ? '场景' : img.assetType === 'prop' ? '道具' : ''}
                                        </div>
                                      )}
                                    </div>
                                  </button>
                                ))}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2" style={{ color: 'var(--color-text-secondary)' }}>
                    <textarea
                      className="nodrag nowheel w-full px-2 py-1 rounded text-2xs resize-vertical"
                      style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-secondary)', minWidth: '290px', minHeight: '60px', lineHeight: '1.4' }}
                      value={shot.videoPrompt || ''}
                      onChange={(e) => handleFieldChange(idx, 'videoPrompt', e.target.value)}
                      onBlur={handleFieldBlur}
                      placeholder="视频提示词"
                      rows={3}
                    />
                  </td>
                  <td className="px-3 py-2">
                    {/* 角色参考图选择 */}
                    {connectedImages.length > 0 ? (
                      <div className="relative">
                        <div
                          className="nodrag w-full px-2 py-1 rounded cursor-pointer"
                          style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border-primary)', minWidth: '120px', minHeight: '32px' }}
                          onClick={() => setRefPopover({ shotIndex: idx, type: 'character' })}
                        >
                          {selectedCharacterRefs.length > 0 ? (
                            <div className="flex gap-1 flex-wrap">
                              {selectedCharacterRefs.map((ref, i) => (
                                <div key={i} className="w-6 h-6 rounded overflow-hidden flex-shrink-0" style={{ border: '1px solid var(--color-border-primary)' }} title={ref.name}>
                                  <img src={ref.url} alt="" className="w-full h-full object-cover" />
                                </div>
                              ))}
                            </div>
                          ) : (
                            <span className="text-2xs" style={{ color: 'var(--color-text-muted)' }}>选择角色</span>
                          )}
                        </div>
                        {/* 弹出选择器 */}
                        {refPopover?.shotIndex === idx && refPopover?.type === 'character' && (
                          <>
                            <div className="fixed inset-0 z-30" onClick={() => setRefPopover(null)} />
                            <div className="absolute z-40 mt-1 rounded-lg p-2 animate-in fade-in zoom-in-95 duration-150" style={{ boxShadow: 'var(--shadow-dropdown)', backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-primary)', minWidth: '200px' }}>
                              <div className="flex items-center justify-between mb-1 px-1">
                                <span className="text-2xs font-bold" style={{ color: 'var(--color-text-muted)' }}>选择角色参考图（多选）</span>
                                <button className="nodrag p-0.5" onClick={() => setRefPopover(null)}>
                                  <X size={12} style={{ color: 'var(--color-text-muted)' }} />
                                </button>
                              </div>
                              <div className="grid grid-cols-3 gap-1">
                                <button
                                  className="nodrag aspect-square rounded flex items-center justify-center text-2xs"
                                  style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border-primary)' }}
                                  onClick={() => handleRefImageSelect(idx, null, 'character')}
                                >
                                  清空
                                </button>
                                {connectedImages.map((img, i) => {
                                  const isSelected = (shot.refCharacterIndexes || []).includes(i);
                                  return (
                                    <button
                                      key={i}
                                      className="nodrag aspect-square rounded overflow-hidden relative"
                                      style={{ border: `2px solid ${isSelected ? accentColor : 'var(--color-border-primary)'}` }}
                                      onClick={() => handleRefImageSelect(idx, i, 'character')}
                                      title={img.name}
                                    >
                                      <img src={img.url} alt="" className="w-full h-full object-cover" />
                                      {isSelected && (
                                        <div className="absolute top-0 right-0 w-4 h-4 rounded-bl flex items-center justify-center" style={{ backgroundColor: accentColor }}>
                                          <span className="text-[var(--color-accent-contrast)] text-2xs">✓</span>
                                        </div>
                                      )}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    ) : (
                      <span className="text-2xs" style={{ color: 'var(--color-text-muted)' }}>无</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {/* 场景参考图选择 */}
                    {connectedImages.length > 0 ? (
                      <div className="relative">
                        <div
                          className="nodrag w-full px-2 py-1 rounded cursor-pointer"
                          style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border-primary)', minWidth: '120px', minHeight: '32px' }}
                          onClick={() => setRefPopover({ shotIndex: idx, type: 'scene' })}
                        >
                          {selectedSceneRefs.length > 0 ? (
                            <div className="flex gap-1 flex-wrap">
                              {selectedSceneRefs.map((ref, i) => (
                                <div key={i} className="w-6 h-6 rounded overflow-hidden flex-shrink-0" style={{ border: '1px solid var(--color-border-primary)' }} title={ref.name}>
                                  <img src={ref.url} alt="" className="w-full h-full object-cover" />
                                </div>
                              ))}
                            </div>
                          ) : (
                            <span className="text-2xs" style={{ color: 'var(--color-text-muted)' }}>选择场景</span>
                          )}
                        </div>
                        {/* 弹出选择器 */}
                        {refPopover?.shotIndex === idx && refPopover?.type === 'scene' && (
                          <>
                            <div className="fixed inset-0 z-30" onClick={() => setRefPopover(null)} />
                            <div className="absolute z-40 mt-1 rounded-lg p-2 animate-in fade-in zoom-in-95 duration-150" style={{ boxShadow: 'var(--shadow-dropdown)', backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-primary)', minWidth: '200px' }}>
                              <div className="flex items-center justify-between mb-1 px-1">
                                <span className="text-2xs font-bold" style={{ color: 'var(--color-text-muted)' }}>选择场景参考图（多选）</span>
                                <button className="nodrag p-0.5" onClick={() => setRefPopover(null)}>
                                  <X size={12} style={{ color: 'var(--color-text-muted)' }} />
                                </button>
                              </div>
                              <div className="grid grid-cols-3 gap-1">
                                <button
                                  className="nodrag aspect-square rounded flex items-center justify-center text-2xs"
                                  style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border-primary)' }}
                                  onClick={() => handleRefImageSelect(idx, null, 'scene')}
                                >
                                  清空
                                </button>
                                {connectedImages.map((img, i) => {
                                  const isSelected = (shot.refSceneIndexes || []).includes(i);
                                  return (
                                    <button
                                      key={i}
                                      className="nodrag aspect-square rounded overflow-hidden relative"
                                      style={{ border: `2px solid ${isSelected ? accentColor : 'var(--color-border-primary)'}` }}
                                      onClick={() => handleRefImageSelect(idx, i, 'scene')}
                                      title={img.name}
                                    >
                                      <img src={img.url} alt="" className="w-full h-full object-cover" />
                                      {isSelected && (
                                        <div className="absolute top-0 right-0 w-4 h-4 rounded-bl flex items-center justify-center" style={{ backgroundColor: accentColor }}>
                                          <span className="text-[var(--color-accent-contrast)] text-2xs">✓</span>
                                        </div>
                                      )}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    ) : (
                      <span className="text-2xs" style={{ color: 'var(--color-text-muted)' }}>无</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {/* 道具参考图选择 */}
                    {connectedImages.length > 0 ? (
                      <div className="relative">
                        <div
                          className="nodrag w-full px-2 py-1 rounded cursor-pointer"
                          style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border-primary)', minWidth: '120px', minHeight: '32px' }}
                          onClick={() => setRefPopover({ shotIndex: idx, type: 'prop' })}
                        >
                          {selectedPropRefs.length > 0 ? (
                            <div className="flex gap-1 flex-wrap">
                              {selectedPropRefs.map((ref, i) => (
                                <div key={i} className="w-6 h-6 rounded overflow-hidden flex-shrink-0" style={{ border: '1px solid var(--color-border-primary)' }} title={ref.name}>
                                  <img src={ref.url} alt="" className="w-full h-full object-cover" />
                                </div>
                              ))}
                            </div>
                          ) : (
                            <span className="text-2xs" style={{ color: 'var(--color-text-muted)' }}>选择道具</span>
                          )}
                        </div>
                        {/* 弹出选择器 */}
                        {refPopover?.shotIndex === idx && refPopover?.type === 'prop' && (
                          <>
                            <div className="fixed inset-0 z-30" onClick={() => setRefPopover(null)} />
                            <div className="absolute z-40 mt-1 rounded-lg p-2 animate-in fade-in zoom-in-95 duration-150" style={{ boxShadow: 'var(--shadow-dropdown)', backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-primary)', minWidth: '200px' }}>
                              <div className="flex items-center justify-between mb-1 px-1">
                                <span className="text-2xs font-bold" style={{ color: 'var(--color-text-muted)' }}>选择道具参考图（多选）</span>
                                <button className="nodrag p-0.5" onClick={() => setRefPopover(null)}>
                                  <X size={12} style={{ color: 'var(--color-text-muted)' }} />
                                </button>
                              </div>
                              <div className="grid grid-cols-3 gap-1">
                                <button
                                  className="nodrag aspect-square rounded flex items-center justify-center text-2xs"
                                  style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border-primary)' }}
                                  onClick={() => handleRefImageSelect(idx, null, 'prop')}
                                >
                                  清空
                                </button>
                                {connectedImages.map((img, i) => {
                                  const isSelected = (shot.refPropIndexes || []).includes(i);
                                  return (
                                    <button
                                      key={i}
                                      className="nodrag aspect-square rounded overflow-hidden relative"
                                      style={{ border: `2px solid ${isSelected ? accentColor : 'var(--color-border-primary)'}` }}
                                      onClick={() => handleRefImageSelect(idx, i, 'prop')}
                                      title={img.name}
                                    >
                                      <img src={img.url} alt="" className="w-full h-full object-cover" />
                                      {isSelected && (
                                        <div className="absolute top-0 right-0 w-4 h-4 rounded-bl flex items-center justify-center" style={{ backgroundColor: accentColor }}>
                                          <span className="text-[var(--color-accent-contrast)] text-2xs">✓</span>
                                        </div>
                                      )}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    ) : (
                      <span className="text-2xs" style={{ color: 'var(--color-text-muted)' }}>无</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(StoryboardEpisodeNode);
